import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { trackingService } from '../../../services/trackingService';
import { getAvailableSlots } from '../../service-booking-wizard/scheduleAvailability';
import { formatFrenchDate, getMinRescheduleDate } from '../appointmentTiming';

const toDateKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const getDurationMinutes = (appointment) => {
  const parse = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    const [h, m] = raw.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const start = parse(appointment?.scheduledTimeStart);
  const end = parse(appointment?.scheduledTimeEnd);
  if (start != null && end != null && end > start) {
    return end - start;
  }
  return 60;
};

const RescheduleModal = ({ appointment, open, onClose, onSuccess }) => {
  const minDate = useMemo(() => getMinRescheduleDate(), [open]);

  const [calendarMonth, setCalendarMonth] = useState(() => new Date(minDate.getFullYear(), minDate.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(minDate);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [scheduleContext, setScheduleContext] = useState({
    technicians: [],
    appointments: [],
    timeOffEvents: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const durationMinutes = getDurationMinutes(appointment);

  useEffect(() => {
    if (!open) return;

    setSelectedDate(minDate);
    setCalendarMonth(new Date(minDate.getFullYear(), minDate.getMonth(), 1));
    setSelectedSlot(null);
    setError('');

    const fetchContext = async () => {
      setLoading(true);
      try {
        const [
          { data: techniciansData, error: techniciansError },
          { data: appointmentsData, error: appointmentsError },
          { data: timeoffData, error: timeoffError },
        ] = await Promise.all([
          supabase
            .from('user_profiles')
            .select('id, full_name, specialty, is_active')
            .eq('role', 'technicien')
            .eq('is_active', true),
          supabase
            .from('appointments')
            .select('id, scheduled_date, scheduled_time_start, scheduled_time_end, technician')
            .not('scheduled_date', 'is', null)
            .not('scheduled_time_start', 'is', null)
            .not('scheduled_time_end', 'is', null),
          supabase.from('technician_timeoff').select('*'),
        ]);

        if (techniciansError) throw techniciansError;
        if (appointmentsError) throw appointmentsError;
        if (timeoffError) throw timeoffError;

        setScheduleContext({
          technicians: techniciansData || [],
          appointments: (appointmentsData || []).filter((row) => row.id !== appointment?.id),
          timeOffEvents: timeoffData || [],
        });
      } catch (fetchError) {
        console.error('Erreur chargement créneaux reprogrammation:', fetchError);
        setError('Impossible de charger les créneaux disponibles.');
      } finally {
        setLoading(false);
      }
    };

    fetchContext();
  }, [open, appointment?.id, minDate]);

  const availableSlots = useMemo(() => {
    if (loading) return [];
    return getAvailableSlots({
      forDate: selectedDate,
      urgencyId: 'tomorrow',
      durationMinutes,
      ...scheduleContext,
    });
  }, [loading, selectedDate, durationMinutes, scheduleContext]);

  const isDateDisabled = (day) => {
    if (!day) return true;
    const candidate = new Date(day);
    candidate.setHours(0, 0, 0, 0);
    return candidate < minDate;
  };

  const buildCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const first = new Date(year, month, 1);
    const startPad = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(new Date(year, month, day));
    }
    return cells;
  };

  const handleDateSelect = (day) => {
    if (isDateDisabled(day)) return;
    const next = new Date(day);
    next.setHours(0, 0, 0, 0);
    setSelectedDate(next);
    setSelectedSlot(null);
    setError('');
  };

  const handleConfirm = async () => {
    if (!selectedSlot || !appointment?.id) {
      setError('Veuillez sélectionner un créneau.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const slotDate = new Date(selectedSlot.date);
      const [startTime, endTime] = selectedSlot.time?.split(' - ') || [];
      const scheduledDate = slotDate.toISOString().slice(0, 10);

      const technicianId = await trackingService.assignBestTechnician({
        scheduled_date: scheduledDate,
        scheduled_time_start: startTime?.trim(),
        scheduled_time_end: endTime?.trim(),
        service_type: appointment.serviceType,
        urgency: 'tomorrow',
        customer_latitude: appointment.customerLatitude,
        customer_longitude: appointment.customerLongitude,
      });

      const { error: updateError } = await supabase
        .from('appointments')
        .update({
          scheduled_date: scheduledDate,
          scheduled_time_start: startTime?.trim(),
          scheduled_time_end: endTime?.trim(),
          technician: technicianId,
          urgency: 'tomorrow',
        })
        .eq('id', appointment.id);

      if (updateError) throw updateError;

      onSuccess?.({
        scheduledDate,
        scheduledTimeStart: startTime?.trim(),
        scheduledTimeEnd: endTime?.trim(),
      });
      onClose();
    } catch (confirmError) {
      console.error('Erreur reprogrammation:', confirmError);
      setError(confirmError.message || 'La reprogrammation a échoué. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-lg border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Reprogrammer l&apos;intervention</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Choisissez une nouvelle date à partir de demain. Les créneaux urgents ne sont pas proposés.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Fermer"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="rounded-xl border border-border p-4 bg-card mb-4">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Mois précédent"
            >
              <Icon name="ChevronLeft" size={18} />
            </button>
            <p className="text-sm font-semibold capitalize">
              {calendarMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </p>
            <button
              type="button"
              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Mois suivant"
            >
              <Icon name="ChevronRight" size={18} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
              <div key={d} className="text-center text-[11px] font-medium text-muted-foreground py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {buildCalendarDays().map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} />;
              const disabled = isDateDisabled(day);
              const selected = toDateKey(day) === toDateKey(selectedDate);
              return (
                <button
                  key={toDateKey(day)}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleDateSelect(day)}
                  className={`aspect-square rounded-lg text-sm font-medium transition-colors ${
                    selected
                      ? 'bg-primary text-primary-foreground'
                      : disabled
                      ? 'text-muted-foreground/40 cursor-not-allowed'
                      : 'hover:bg-primary/10 text-foreground'
                  }`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            Date sélectionnée :{' '}
            <span className="font-medium text-foreground">{formatFrenchDate(selectedDate)}</span>
          </p>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-semibold text-foreground mb-2">Créneaux disponibles</h4>
          {loading ? (
            <p className="text-sm text-muted-foreground">Chargement des créneaux...</p>
          ) : availableSlots.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucun créneau disponible pour cette date. Choisissez un autre jour.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {availableSlots.map((slot) => {
                const isSelected = selectedSlot?.id === slot.id;
                return (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => {
                      setSelectedSlot(slot);
                      setError('');
                    }}
                    className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    {slot.time}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-error mb-4">{error}</p>
        )}

        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            variant="default"
            fullWidth
            onClick={handleConfirm}
            disabled={saving || !selectedSlot}
            loading={saving}
          >
            Confirmer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleModal;
